// Frontend/src/lib/issueAttachments.ts
//
// The attachment rules and the state that enforces them.
//
// Not in IssueBits.tsx, where they started: a module exporting both
// components and plain functions breaks React Fast Refresh, and the linter
// is right to say so -- editing a chip would remount every form on the page.
// The same reason useIssueLabels lives in issueLabels.ts.
//
// Both create forms grew their own copy of these limits. Two copies of a
// rule that must mirror issues/serializers.py is one too many: the day the
// server starts accepting a new type and one form still rejects it, nobody
// will think to look in the other file.

import React from "react";
import { useLanguage } from "@/contexts/LanguageContext";

/** Mirrors issues/serializers.py. The server is the guard; this only saves a
 *  10MB upload spent to be told no. */
export const MAX_ATTACHMENTS = 5;
export const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;
export const ALLOWED_ATTACHMENT_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "application/pdf",
];

/**
 * File selection state plus the client-side pre-check.
 *
 * Returns the first failure as a translated string rather than throwing: a
 * rejected file is an ordinary thing a person does, not an exception.
 */
export function useAttachments() {
  const { t } = useLanguage();
  const [files, setFiles] = React.useState<File[]>([]);
  const [error, setError] = React.useState("");

  const add = React.useCallback(
    (list: FileList | null) => {
      setError("");
      const incoming = Array.from(list ?? []);
      if (!incoming.length) return;

      setFiles((current) => {
        if (current.length + incoming.length > MAX_ATTACHMENTS) {
          setError(t("issues.tooManyFiles"));
          return current;
        }
        for (const f of incoming) {
          if (!ALLOWED_ATTACHMENT_TYPES.includes(f.type)) {
            setError(t("issues.badFileType"));
            return current;
          }
          if (f.size > MAX_ATTACHMENT_BYTES) {
            setError(t("issues.fileTooLarge"));
            return current;
          }
        }
        return [...current, ...incoming];
      });
    },
    [t]
  );

  const remove = React.useCallback((index: number) => {
    setError("");
    setFiles((current) => current.filter((_, i) => i !== index));
  }, []);

  const reset = React.useCallback(() => {
    setFiles([]);
    setError("");
  }, []);

  return { files, error, add, remove, reset };
}

